import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  HEADER_HEIGHT,
  LOADING_BACKDROP_STYLE,
  PRINT_REPORT_BREAK_AVOID,
  PRINT_REPORT_NOT_DISPLAY,
  PRINT_REPORT_PAGE,
} from '../../_constants/styleConst';
import {
  INSPECTION_STATUSES,
  SEND_EXFM_TYPE,
} from '../../_constants/inspectionConst';
import { useTranslation } from 'react-i18next';
import ReportBasicInfo from '../../_common/Report/ReportBasicInfo';
import {
  countTableRows,
  createReportTable,
  getExFMUrl,
  getMidIndex,
  getStatusAndQcCountOnRevert,
} from '../../_utils/inspectionUtils';
import ReportDtaImage from '../../_common/Report/ReportDtaImage';
import ResultTable, {
  CategoryResultTable,
} from '../../_common/Report/ResultTable';
import { fetchReport } from '../../stores/report/slice';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmModal from '../../_common/Modal/ConfirmModal';
import { fetchInspectionTop } from '../../stores/inspectionTop/slice';
import { formatDate, isInspection } from '../../_utils/utils';
import { INSPEX_PATHNAME, STATUS } from '../../_constants/Const';
import {
  postSendExFM,
  postUploadFileToExFM,
} from '../../services/api/sendExFM';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { isManagerOrAdmin } from '../../_utils/utils';
import { isRejected } from '@reduxjs/toolkit';
import {
  isGetInspectionTop,
  isGetRepairRequest,
  isGetReport,
} from '../../_utils/typeGuardUtils';
import useResetStore from '../../hooks/useResetStore';
import { NavigateReport } from '../../types/NavigateOptionType';
import { API_CODE } from '../../_constants/apiConst';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import { fetchRepairRequest } from '../../stores/repairRequest/slice';
import { ExFMError } from '../../services/ExFMError';
import { ExFMMessageInfo } from '../../services/apiTypes';
import ExFMMessageModal from './components/ExFMMessageModal';

const Report: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const navigateOption = location.state as NavigateReport;
  const { error, showApiError, showTypeError, showClientError, hideError } =
    useErrorModal();
  const updateStatus = useUpdateStatus();
  const { resetInspectionStore } = useResetStore();

  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const { report } = useSelector((state: RootState) => state.report);

  /** 完了の確認ダイアログ */
  const [isOpenComplete, setIsOpenComplete] = useState(false);
  /** 解除の確認ダイアログ */
  const [isOpenRevert, setIsOpenRevert] = useState(false);
  /** ExFMメッセージ用ダイアログ */
  const [isOpenMessage, setIsOpenMessage] = useState(false);
  /** ロード中か */
  const [isLoading, setIsLoading] = useState(false);
  /** 表示する帳票の出荷検査回数(0 : 受入診断) */
  const [qcCount, setQcCount] = useState(0);
  /** ドロップダウンのリスト */
  const [qcCountList, setQcCountList] = useState<number[]>([0]);
  /** 画面に表示する用の出荷検査回数 */
  const [displayQcCount, setDisplayQcCount] = useState(0);
  /**
   * テーブルに表示するデータ
   * reportTable[0] : 左側
   * reportTable[1] : 右側
   */
  const [reportTables, setReportTables] = useState<CategoryResultTable[][]>([]);
  /** ドキュメントNo */
  const [documentNo, setDocumentNo] = useState<string>();
  /** 有効日付 */
  const [effectiveDate, setEffectiveDate] = useState<string>();
  /** 戻るボタン */
  const [isShowBackButton, setIsShowBackButton] = useState<boolean>(false);
  /** ExFM連携後のメッセージと部品情報 */
  const [exfmMessageInfo, setExfmMessageInfo] = useState<ExFMMessageInfo>({
    message: '',
    partsList: [],
  });

  useEffect(() => {
    if (!repairRequest) {
      return;
    }
    // 出荷検査の場合はドロップダウンリストを作成
    if (!isInspection(repairRequest.status)) {
      createReportList(repairRequest.rmaNo, repairRequest.status);
    }
    // 遷移元から指定があれば初期表示時にその帳票を表示する
    if (navigateOption && navigateOption.defaultQcCount !== undefined) {
      setQcCount(navigateOption.defaultQcCount);
      fetchReportData(repairRequest.rmaNo, navigateOption.defaultQcCount);
    }
  }, [repairRequest?.rmaNo]);

  useEffect(() => {
    if (
      repairRequest &&
      (repairRequest.status === INSPECTION_STATUSES.INSPECTION_NOT_LINKED ||
        repairRequest.status === INSPECTION_STATUSES.QC_NOT_LINKED)
    ) {
      setIsShowBackButton(true);
    }
  }, [repairRequest?.status]);

  /** 診断TopのデータのqcCountから帳票のドロップダウンリストを作成 */
  const createReportList = async (rmaNo: string, status: number) => {
    const response = await dispatch(
      fetchInspectionTop({
        query: { rmaNo },
        isInspection: false,
      }),
    );

    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.QC_TOP, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
      return;
    }
    if (!isGetInspectionTop(response.payload)) {
      showTypeError(API_CODE.QC_TOP);
      return;
    }
    const times = response.payload.qcCount;
    const list = Array.from({ length: times + 1 }, (_, i) => i);
    // 進行中及びqcCountが1増えた時は最新のものは表示しない
    if (
      status === INSPECTION_STATUSES.QC_IN_PROGRESS ||
      status === INSPECTION_STATUSES.QC_CONFIRM ||
      status === INSPECTION_STATUSES.QC_REPAIRING
    ) {
      list.pop();
    }
    setQcCountList(list);
  };

  /** 帳票データ取得 */
  const fetchReportData = async (rmaNo: string, qcCount: number) => {
    const response = await dispatch(
      fetchReport({ rmaNo, qcCount: qcCount === 0 ? undefined : qcCount }),
    );
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPORT);
      return;
    }
    if (!isGetReport(response.payload)) {
      showTypeError(API_CODE.REPORT);
      return;
    }

    setDisplayQcCount(qcCount);
    setDocumentNo(response.payload.documentNo);
    setEffectiveDate(response.payload.effectiveDate);
    // テーブルのデータをセット
    const reportTableAll = createReportTable(response.payload, qcCount);
    const tableRowList = countTableRows(reportTableAll);
    const midIndex = getMidIndex(tableRowList);
    setReportTables([
      reportTableAll.slice(0, midIndex),
      reportTableAll.slice(midIndex),
    ]);
  };

  /** 表示する帳票の変更 */
  const onChangeQcCount = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setQcCount(Number(event.target.value));
  };

  /** 表示ボタン押下 */
  const onClickDisplay = async () => {
    if (repairRequest) {
      fetchReportData(repairRequest.rmaNo, qcCount);
    }
  };

  /** 完了ボタン押下時 */
  const onClickCompleteButton = () => {
    if (
      !repairRequest ||
      !repairRequest.isCreatedByExFM ||
      !repairRequest.exFMUrl
    ) {
      navigate(INSPEX_PATHNAME.RMA_SEARCH);
      return;
    }
    if (
      // 連携が必要で未連携のステータス、再修理
      (repairRequest.status === INSPECTION_STATUSES.INSPECTION_NOT_LINKED &&
        !repairRequest.linkedInspectionToExFM) ||
      repairRequest.status === INSPECTION_STATUSES.QC_REPAIRING ||
      (repairRequest.status === INSPECTION_STATUSES.QC_NOT_LINKED &&
        !repairRequest.linkedQcToExFM)
    ) {
      // ExFM連携の確認ダイアログを開く
      setIsOpenComplete(true);
      return;
    }
    navigate(INSPEX_PATHNAME.RMA_SEARCH);
  };

  /** 完了の確認ダイアログのOK押下時 */
  const onClickOkCompleteModal = async () => {
    setIsOpenComplete(false);

    if (!repairRequest) {
      return;
    }

    // 返品修理時にはデータは連携せず、ExFMに遷移する
    if (repairRequest.status === INSPECTION_STATUSES.QC_REPAIRING) {
      window.location.href = getExFMUrl(repairRequest);
      return;
    }

    setIsLoading(true);
    // ExFMのBlobストレージへのファイルアップロード
    for (const filePath of report.filepaths) {
      try {
        await postUploadFileToExFM({
          rmaNo: repairRequest.rmaNo,
          filePath,
        });
      } catch (error) {
        if (error instanceof ExFMError) {
          // ExFMから返されたメッセージとコードを表示
          showClientError(
            `${error.errorMessage}(${t('LBL_ERROR_CODE')}:${error.errorCode})`,
          );
        } else {
          showApiError(error, API_CODE.UPLOAD_FILE_EXFM);
        }
        setIsLoading(false);
        return;
      }
    }

    try {
      // ExFMにデータ連携
      const exfmResponse = await postSendExFM({
        rmaNo: repairRequest.rmaNo,
        type: isInspection(repairRequest.status)
          ? SEND_EXFM_TYPE.INSPECTION
          : SEND_EXFM_TYPE.QC,
      });

      // メッセージがあればダイアログを開く
      if (exfmResponse && exfmResponse.message) {
        setExfmMessageInfo(exfmResponse);
        setIsOpenMessage(true);
        return;
      }

      // 最新のrepairRequestを取得する
      const response = await dispatch(
        fetchRepairRequest({ rmaNo: repairRequest.rmaNo }),
      );
      if (isRejected(response)) {
        showApiError(response.payload, API_CODE.REPAIR_REQUEST);
        return;
      }
      if (isGetRepairRequest(response.payload)) {
        // ExFMを開く
        // 同一タブ上で遷移させる
        window.location.href = getExFMUrl(response.payload);
      } else {
        showTypeError(API_CODE.REPAIR_REQUEST);
        return;
      }
    } catch (error) {
      if (error instanceof ExFMError) {
        // ExFMから返されたメッセージとコードを表示
        showClientError(
          `${error.errorMessage}(${t('LBL_ERROR_CODE')}:${error.errorCode})`,
        );
      } else {
        showApiError(error, API_CODE.SEND_EXFM);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /** ExFMメッセージモーダルを閉じる */
  const onClickOkMessageModal = async () => {
    setIsOpenMessage(false);
    if (!repairRequest) {
      return;
    }

    // 最新のrepairRequestを取得する
    const response = await dispatch(
      fetchRepairRequest({ rmaNo: repairRequest.rmaNo }),
    );
    if (isRejected(response)) {
      showApiError(response.payload, API_CODE.REPAIR_REQUEST);
      return;
    }
    if (isGetRepairRequest(response.payload)) {
      // ExFMを開く
      // 同一タブ上で遷移させる
      window.location.href = getExFMUrl(response.payload);
    } else {
      showTypeError(API_CODE.REPAIR_REQUEST);
      return;
    }
  };

  /** ドロップダウンでラベルを表示 */
  const getLabelQcCount = (qcCount: number) => {
    return qcCount === 0
      ? t('LBL_REPORT_SELECT_INSPECTION')
      : t('LBL_REPORT_SELECT_QC_COUNT', { 0: qcCount });
  };

  /** 表示用のステータスに変換 */
  const getLabelStatus = (status: number) => {
    // 0 : 進行中
    // 1 : 完了
    return t(
      STATUS[
        status === INSPECTION_STATUSES.NO_FAULT_FOUND ||
        status === INSPECTION_STATUSES.INSPECTION_COMPLETE ||
        status === INSPECTION_STATUSES.QC_REPAIRING ||
        status === INSPECTION_STATUSES.QC_COMPLETE
          ? 1
          : 0
      ],
    );
  };

  const getMessengerRevert = () => {
    if (!repairRequest) return '';
    const currentQcCount = inspectionTop.qcCount;
    const currentStatus = repairRequest.status;
    const [newStatus, newQcCount] = getStatusAndQcCountOnRevert(
      currentStatus,
      currentQcCount,
    );
    const displayQcRevert =
      currentStatus === INSPECTION_STATUSES.QC_REPAIRING
        ? currentQcCount - 1
        : currentQcCount;
    const currentData = `${getLabelQcCount(displayQcRevert)}(${getLabelStatus(currentStatus)})`;
    const newData = `${getLabelQcCount(newQcCount)}(${getLabelStatus(newStatus)})`;
    return t('MSG_REVERT_STATUS', {
      0: currentData,
      1: newData,
    });
  };

  const handleDownloadPdf = async () => {
    if (!repairRequest) {
      return;
    }
    const title = document.title;
    if (displayQcCount === 0) {
      // 受入診断
      document.title = repairRequest.rmaNo + '_Inspection_Report.pdf';
    } else {
      // 出荷検査
      document.title =
        repairRequest.rmaNo + '_QC#' + displayQcCount + '_Report.pdf';
    }
    window.print();
    document.title = title;
  };

  /** 解除の確認ダイアログのOK押下時 */
  const onClickOkRevertModal = async () => {
    setIsOpenRevert(false);
    if (!repairRequest) {
      return;
    }
    const [status, qcCount] = getStatusAndQcCountOnRevert(
      repairRequest.status,
      inspectionTop.qcCount,
    );
    // ステータス更新
    const isSuccess = await updateStatus(status, qcCount, showApiError);
    if (isSuccess) {
      // 診断データをリセット
      resetInspectionStore();
      navigate(INSPEX_PATHNAME.INSPECTION_TOP);
    }
  };

  /** 完了の確認ダイアログメッセージ */
  const getMessengerComplete = () => {
    if (repairRequest) {
      // 受入診断完了
      if (repairRequest.status === INSPECTION_STATUSES.INSPECTION_NOT_LINKED) {
        return t('MSG_SEND_EXFM');
      }
      // 返品修理
      if (repairRequest.status === INSPECTION_STATUSES.QC_REPAIRING) {
        return t('MSG_RETURN_EXFM');
      }
      // 検査完了
      return t('MSG_QC_COMPLETE_EXFM');
    }
    return '';
  };

  /** 戻るボタン押下時 */
  const onClickBack = async () => {
    if (!repairRequest) {
      return;
    }
    // 受入診断未連携 → パーツ選択
    // 出荷検査未連携 → 出荷検査中
    const isSuccess = await updateStatus(
      isInspection(repairRequest.status)
        ? INSPECTION_STATUSES.INSPECTION_PARTS_SELECT
        : INSPECTION_STATUSES.QC_IN_PROGRESS,
      isInspection(repairRequest.status) ? 0 : inspectionTop.qcCount,
      showApiError,
    );
    if (isSuccess) {
      if (isInspection(repairRequest.status)) {
        navigate(INSPEX_PATHNAME.PARTS_SELECTION);
      } else {
        // 診断データをリセット
        resetInspectionStore();
        navigate(INSPEX_PATHNAME.INSPECTION_TOP);
      }
    }
  };

  return (
    <Box
      sx={[
        PRINT_REPORT_PAGE,
        {
          mt: `calc(${HEADER_HEIGHT} + 1rem)`,
          mb: '1%',
        },
      ]}
    >
      <Box width="100%" sx={PRINT_REPORT_NOT_DISPLAY}>
        <TextField
          select
          label={t('LBL_REPORT')}
          value={qcCountList.includes(qcCount) ? qcCount : 0}
          onChange={onChangeQcCount}
          size="small"
          sx={{ ml: '1.5rem', mr: '1rem', width: '20%' }}
        >
          {qcCountList.map((item, i) => (
            <MenuItem key={`qc_count_${i}`} value={item}>
              {getLabelQcCount(item)}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={onClickDisplay}>
          {t('BTN_DISPLAY')}
        </Button>
      </Box>
      {reportTables.length > 0 && (
        <Box mx="2rem">
          <Box
            sx={{
              whiteSpace: 'pre-wrap !important',
              wordWrap: 'break-word !important',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography pt="1rem" variant="h3">
                {t('LBL_INSPECTION_SHEET')}
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                mt="2rem"
                border="solid 1px"
              >
                <Grid container spacing={1}>
                  <Grid
                    size={12}
                    sx={{ display: 'flex' }}
                    style={{ padding: '5px 2px' }}
                    borderBottom={'solid 1px'}
                  >
                    <Typography>{`${t('LBL_DOCUMENT_NO')} : `}</Typography>
                    <Typography>{documentNo}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={1}>
                  <Grid
                    size={12}
                    sx={{ display: 'flex' }}
                    style={{ padding: '5px 2px' }}
                  >
                    <Typography>{`${t('LBL_EFFECTIVE_DATE')} : `}</Typography>
                    <Typography>
                      {formatDate(effectiveDate, userData.dateFormat)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <ReportBasicInfo
              rmaNo={report.rmaNo}
              serialNo={report.serialNo}
              model={report.model}
              customerName={report.customerName}
              originalCustomer={report.originalCustomer}
              inspector={report.inspector}
              qcInspector={report.qcInspector}
              inspectionDate={report.inspectionDate}
              qcDate={report.qcDate}
              qcCount={displayQcCount}
              isInspectionSerialChecked={report.isInspectionSerialChecked}
              isQcSerialChecked={report.isQcSerialChecked}
            />
            <Box display="flex" justifyContent="space-between" mt="2rem">
              <Box width="50%" mr={'5px'}>
                <ResultTable tableData={reportTables[0]} isPreview={false} />
              </Box>
              <Box width="50%" ml={'5px'}>
                <ResultTable tableData={reportTables[1]} isPreview={false} />
                <ReportDtaImage
                  markers={report.markers}
                  repairRequest={repairRequest}
                />
              </Box>
            </Box>
            <Box my={3} sx={PRINT_REPORT_BREAK_AVOID}>
              <Typography
                pl={1}
                pt={1}
                fontSize={'0.7rem'}
                fontWeight={400}
                color="grey.500"
              >
                {t('LBL_MEMO')}
              </Typography>
              <Box
                border={1}
                borderColor={'grey.500'}
                borderRadius={'4px'}
                p={'16px 14px'}
                sx={{ minHeight: '120px' }}
              >
                {report.memo}
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={PRINT_REPORT_NOT_DISPLAY}
          >
            {/* 確定解除と戻るボタン  */}
            <Box sx={{ display: 'flex' }}>
              {isShowBackButton && (
                <Button
                  sx={{
                    width: 'fit-content',
                    minWidth: '140px',
                    p: '0.5rem 1rem',
                    mr: 3,
                  }}
                  variant="outlined"
                  onClick={() => onClickBack()}
                >
                  {t('BTN_BACK')}
                </Button>
              )}
              {isManagerOrAdmin(userData) && (
                <Button
                  sx={{
                    width: 'fit-content',
                    minWidth: '140px',
                    p: '0.5rem 1rem',
                  }}
                  variant="contained"
                  color="error"
                  onClick={() => setIsOpenRevert(true)}
                >
                  {t('BTN_REVERT_STATUS')}
                </Button>
              )}
            </Box>
            {/* タウンロードと完了ボタン */}
            <Box sx={{ display: 'flex' }}>
              <Button
                sx={{
                  width: 'fit-content',
                  minWidth: '140px',
                  p: '0.5rem 1rem',
                  mr: 3,
                }}
                variant="outlined"
                onClick={() => {
                  handleDownloadPdf();
                }} // TODO 仕向けによってA4/Letterを選択させたい
              >
                {t('BTN_PRINT')}
              </Button>
              <Button
                sx={{
                  width: 'fit-content',
                  minWidth: '140px',
                  p: '0.5rem 1rem',
                }}
                variant="contained"
                onClick={onClickCompleteButton}
              >
                {t('BTN_COMPLETE')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <ConfirmModal
        isOpen={isOpenComplete}
        displayMessage={getMessengerComplete()}
        onClickCancel={() => setIsOpenComplete(false)}
        onClickOk={onClickOkCompleteModal}
      />
      <ConfirmModal
        isOpen={isOpenRevert}
        displayMessage={getMessengerRevert()}
        onClickCancel={() => setIsOpenRevert(false)}
        onClickOk={onClickOkRevertModal}
      />
      <ExFMMessageModal
        isOpen={isOpenMessage}
        onClose={() => onClickOkMessageModal()}
        messageInfo={exfmMessageInfo}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default Report;
